import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Coffee,
  Cookie,
  CupSoda,
  Droplets,
  Smile,
  Utensils,
  Zap,
} from "lucide-react"
import { useCallback, useRef, useState } from "react"

interface CheckinPayload {
  type: "mood" | "energy"
  level: number
}

interface ActionSheetProps {
  open: boolean
  onClose: () => void
  onQuickLog?: (item: string) => void
  onCheckin?: (item: CheckinPayload) => void
  onLogEntry?: (item: string) => void
}

const quickLogItems = [
  { label: "Water", icon: Droplets, iconVar: "--fig-entry-water-icon" },
  { label: "Coffee", icon: Coffee, iconVar: "--fig-entry-beverage-icon" },
  { label: "Tea", icon: CupSoda, iconVar: "--fig-entry-beverage-icon" },
  { label: "Snack", icon: Cookie, iconVar: "--fig-entry-meal-icon" },
]

const checkinItems = [
  {
    label: "Mood",
    description: "How are you feeling?",
    icon: Smile,
    iconVar: "--fig-entry-mood-icon",
  },
  {
    label: "Energy",
    description: "Energy level?",
    icon: Zap,
    iconVar: "--fig-entry-mood-icon",
  },
]

const logEntryItems = [
  {
    label: "Meal",
    description: "Photo, voice, or browse ingredients",
    icon: Utensils,
    iconVar: "--fig-entry-meal-icon",
  },
  {
    label: "Symptom",
    description: "Bloating, cramping, nausea...",
    icon: CircleAlert,
    iconVar: "--fig-entry-symptom-icon",
  },
  {
    label: "Stool",
    description: "Bristol stool chart",
    icon: Droplets,
    iconVar: "--fig-entry-stool-icon",
  },
]

const moodOptions = [
  { level: 1, emoji: "😫", label: "Awful" },
  { level: 2, emoji: "😕", label: "Low" },
  { level: 3, emoji: "😐", label: "Okay" },
  { level: 4, emoji: "🙂", label: "Good" },
  { level: 5, emoji: "😊", label: "Great" },
]

const energyOptions = [
  { level: 1, emoji: "🪫", label: "Drained" },
  { level: 2, emoji: "😴", label: "Low" },
  { level: 3, emoji: "⚡", label: "Moderate" },
  { level: 4, emoji: "💪", label: "High" },
  { level: 5, emoji: "🔥", label: "Wired" },
]

export function ActionSheet({
  open,
  onClose,
  onQuickLog,
  onCheckin,
  onLogEntry,
}: ActionSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [pickerMode, setPickerMode] = useState<null | "mood" | "energy">(null)

  // Wrap onClose to also reset picker mode, so state is clean on next open
  const handleClose = useCallback(() => {
    setPickerMode(null)
    onClose()
  }, [onClose])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        handleClose()
      }
    },
    [handleClose],
  )

  const handleQuickTap = useCallback(
    (label: string, btn: HTMLButtonElement) => {
      btn.classList.add("flash-confirm")
      setTimeout(() => {
        onQuickLog?.(label)
        handleClose()
        btn.classList.remove("flash-confirm")
      }, 400)
    },
    [onQuickLog, handleClose],
  )

  const handleCheckinOptionTap = useCallback(
    (type: "mood" | "energy", level: number, btn: HTMLButtonElement) => {
      btn.classList.add("flash-confirm")
      setTimeout(() => {
        onCheckin?.({ type, level })
        handleClose()
        btn.classList.remove("flash-confirm")
      }, 400)
    },
    [onCheckin, handleClose],
  )

  const pickerOptions = pickerMode === "mood" ? moodOptions : energyOptions
  const pickerTitle =
    pickerMode === "mood" ? "How are you feeling?" : "Energy level?"

  return (
    <div
      ref={overlayRef}
      className={`action-sheet-overlay fixed inset-0 z-30 ${open ? "open" : ""}`}
      style={{ background: "#00000040", overscrollBehavior: "contain" }}
      onClick={handleOverlayClick}
    >
      <div
        className="action-sheet-panel fixed right-0 bottom-0 left-0"
        style={{
          background: "var(--fig-action-sheet-bg)",
          borderRadius: "20px 20px 0 0",
          boxShadow: "var(--fig-action-sheet-shadow)",
          padding: "12px 20px 32px",
          maxHeight: "85dvh",
          overflowY: "auto",
        }}
      >
        {/* Handle */}
        <div className="mb-4 flex justify-center">
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: "var(--fig-week-dot-empty)",
            }}
          />
        </div>

        {pickerMode ? (
          /* Mini Picker Mode */
          <div>
            {/* Back button + title */}
            <div className="mb-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPickerMode(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 0",
                  color: "var(--fig-text-inactive)",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
                aria-label="Back"
              >
                <ChevronLeft size={16} />
                <span style={{ fontSize: 13, fontWeight: 500 }}>Back</span>
              </button>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--fig-text-primary)",
                  marginLeft: 4,
                }}
              >
                {pickerTitle}
              </span>
            </div>

            {/* Emoji options */}
            <div className="grid grid-cols-5 gap-2">
              {pickerOptions.map((option) => (
                <button
                  key={option.level}
                  type="button"
                  className="flex flex-col items-center gap-1.5 rounded-xl border-none p-3"
                  style={{
                    background: "var(--fig-action-item-hover)",
                    cursor: "pointer",
                  }}
                  onClick={(e) =>
                    handleCheckinOptionTap(
                      pickerMode,
                      option.level,
                      e.currentTarget,
                    )
                  }
                  aria-label={`${pickerMode} level ${option.level}: ${option.label}`}
                >
                  <span style={{ fontSize: 26 }} aria-hidden="true">
                    {option.emoji}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: "var(--fig-text-primary)",
                    }}
                  >
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Default Mode */
          <>
            {/* Quick Log */}
            <div className="mb-4">
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Quick log
              </span>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {quickLogItems.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="flex flex-col items-center gap-1 rounded-xl border-none p-3"
                    style={{
                      background: "var(--fig-action-item-hover)",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleQuickTap(item.label, e.currentTarget)}
                  >
                    <item.icon
                      size={22}
                      style={{ color: `var(${item.iconVar})` }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--fig-text-primary)",
                      }}
                    >
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Check-in */}
            <div className="mb-4">
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Check-in
              </span>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {checkinItems.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="flex items-center gap-3 rounded-xl border-none p-3 text-left"
                    style={{
                      background: "var(--fig-action-item-hover)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setPickerMode(
                        item.label.toLowerCase() as "mood" | "energy",
                      )
                    }
                  >
                    <item.icon
                      size={22}
                      style={{ color: `var(${item.iconVar})` }}
                    />
                    <div>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--fig-text-primary)",
                          display: "block",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "var(--fig-text-avatar)",
                        }}
                      >
                        {item.description}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Log Entry */}
            <div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Log entry
              </span>
              <div className="mt-2 flex flex-col gap-1.5">
                {logEntryItems.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="flex w-full items-center gap-3 rounded-xl border-none p-3 text-left"
                    style={{
                      background: "var(--fig-action-item-hover)",
                      cursor: "pointer",
                    }}
                    onClick={() => onLogEntry?.(item.label)}
                  >
                    <item.icon
                      size={22}
                      style={{ color: `var(${item.iconVar})` }}
                    />
                    <div className="flex-1">
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--fig-text-primary)",
                          display: "block",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "var(--fig-text-avatar)",
                        }}
                      >
                        {item.description}
                      </span>
                    </div>
                    <ChevronRight
                      size={16}
                      style={{ color: "var(--fig-text-placeholder)" }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
